import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Instagram, X } from 'lucide-react';
import Masonry from 'react-masonry-css';
import RadialSectionNav from '../../page_assets/RadialSectionNav';
import SectionNavBar from '../../page_assets/SectionNavBar';

// react-masonry-css reads these as max-widths, so `default` is what anything wider than
// the largest key gets.
const breakpointColumns = {
  default: 4,
  1280: 3,
  1024: 3,
  640: 2,
};

// Must track the largest value above: section measurement reads this many tiles to find
// where a band starts.
const MAX_COLUMNS = 4;

// Clearance left above a section when jumping to it, so its first row doesn't land
// under the fixed header.
const SCROLL_MARGIN = 96;

// How far down the viewport the line sits that decides which section you are in. A
// third of the way down rather than at the top edge: the section you are reading is
// the one filling the screen, not the one whose last tile is still scrolling out.
const ACTIVE_LINE = 0.35;

function ArtPiece({ image, index, onOpen, tileRef, onResize }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const nodeRef = useRef(null);

  // The parent wants this same element for section measurement, so its callback is
  // folded into ours — read through a ref at call time because the parent passes a fresh
  // arrow every render, and depending on it directly would make React detach and reattach
  // the ref on every one.
  const parentRef = useRef(tileRef);
  parentRef.current = tileRef;
  const setNode = useCallback((el) => {
    nodeRef.current = el;
    parentRef.current?.(el);
  }, []);

  // One-shot: the observer disconnects on the first crossing, so tiles don't re-animate
  // when you scroll back up past them.
  useEffect(() => {
    const el = nodeRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setInView(true);
        observer.disconnect();
      },
      // Held back until the tile is a little way in rather than the instant its top
      // edge clears — otherwise the animation is mostly over before it is on screen.
      { rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Both conditions, or the tile animates itself in while still empty: these images
  // are lazy, so arriving on screen and having pixels to show are separate moments.
  const revealed = inView && imageLoaded;

  return (
    <button
      ref={setNode}
      type="button"
      onClick={onOpen}
      aria-label={`Zoom in on ${image.title || `art piece ${index + 1}`}`}
      // The reveal is an animation rather than a transition so it doesn't have to
      // share a duration with the hover treatment on this same element.
      className={`block w-full cursor-zoom-in relative overflow-hidden rounded-lg border border-gray-700 hover:border-pink-500 focus:outline-none focus-visible:border-pink-500 transition-all duration-300 hover:shadow-xl bg-gray-900 mb-3 sm:mb-6 ${
        revealed ? 'animate-tile-in' : 'opacity-0'
      }`}
    >
      {/* No min/max clamp on the image: in a masonry column the natural aspect ratio is
          what staggers the rows. */}
      <img
        src={image.url}
        alt={image.alt || image.title || `Art piece ${index + 1}`}
        loading="lazy"
        className="block w-full h-auto"
        // A lazy image arriving gives its tile a height for the first time and pushes
        // everything below it down, so every section boundary under it has just moved.
        onLoad={() => {
          setImageLoaded(true);
          onResize?.();
        }}
        // A picture that fails to load still has to release its tile, which is
        // otherwise held at opacity 0 for good waiting on a load that won't come.
        onError={() => setImageLoaded(true)}
      />
    </button>
  );
}

/**
 * Full-screen view of one piece. Closes on the button, a click on the backdrop, or Escape.
 *
 * Portalled onto <body>, and it has to be: every route sits inside PageTransition's
 * `.animate-fadeIn`, whose keyframes leave a transform on the wrapper for good
 * (fill-mode forwards). A transformed ancestor becomes the containing block for
 * `position: fixed`, so in place this overlay would size itself to the whole page rather
 * than the viewport.
 */
function Lightbox({ image, onClose }) {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    // Clicks land here rather than on the image itself, so anywhere in the margin closes
    // but the picture and caption stay put.
    <div
      role="dialog"
      aria-modal="true"
      aria-label={image.title || 'Art piece'}
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col items-center justify-center p-4 sm:p-8 cursor-zoom-out"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 text-pink-200 hover:text-pink-400 transition-colors duration-200"
      >
        <X className="w-8 h-8" />
      </button>

      <img
        src={image.url}
        alt={image.alt || image.title || 'Art piece'}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-[80vh] object-contain cursor-default"
      />

      {(image.title || image.description) && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="mt-4 text-center max-w-2xl cursor-default"
        >
          {image.title && (
            <h4 className="text-xl font-bold text-pink-200 mb-1">{image.title}</h4>
          )}
          {image.description && (
            <p className="text-pink-100 text-sm leading-relaxed serif">
              {image.description}
            </p>
          )}
        </div>
      )}
    </div>,
    document.body
  );
}

/**
 * One continuous masonry of every piece. The images arrive ordered by category, and
 * react-masonry-css deals them across columns round-robin, so reading left-to-right,
 * top-to-bottom still walks the categories in order — the run just isn't broken up by
 * headings.
 */
function ArtPortfolioComponent({ images = [], instagramHandles = [], sections = [] }, ref) {
  const [zoomedIndex, setZoomedIndex] = useState(null);
  const closeLightbox = useCallback(() => setZoomedIndex(null), []);

  const galleryRef = useRef(null);
  const tileRefs = useRef([]);
  // Document-space y of each section's first row. A ref rather than state because it is
  // rewritten every animation frame of a scroll, and rendering on it would mean a render
  // per frame for a number nothing displays.
  const sectionTops = useRef([]);
  const frame = useRef(null);

  const [activeSection, setActiveSection] = useState(0);
  const [navVisible, setNavVisible] = useState(false);

  const measure = useCallback(() => {
    // The masonry deals tiles round-robin, so the first MAX_COLUMNS tiles of a category
    // are its first tile in each column and the highest of them is the top of the band.
    // Reading more than the column count is harmless — the extras are second-row tiles in
    // columns already counted, so they can only be lower.
    sectionTops.current = sections.map(({ startIndex }) => {
      const firstRow = tileRefs.current
        .slice(startIndex, startIndex + MAX_COLUMNS)
        .filter(Boolean);
      if (!firstRow.length) return null;
      return Math.min(...firstRow.map((el) => el.getBoundingClientRect().top)) + window.scrollY;
    });

    const line = window.scrollY + window.innerHeight * ACTIVE_LINE;
    let current = 0;
    sectionTops.current.forEach((top, index) => {
      if (top !== null && top <= line) current = index;
    });
    setActiveSection(current);

    // The wheel belongs to the gallery, not to the hero above it.
    const gallery = galleryRef.current;
    setNavVisible(!!gallery && gallery.getBoundingClientRect().top < window.innerHeight * 0.6);
  }, [sections]);

  const remeasure = useCallback(() => {
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      measure();
    });
  }, [measure]);

  useEffect(() => {
    if (!sections.length) return undefined;

    measure();
    window.addEventListener('scroll', remeasure, { passive: true });
    window.addEventListener('resize', remeasure);

    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
      window.removeEventListener('scroll', remeasure);
      window.removeEventListener('resize', remeasure);
    };
  }, [sections, measure, remeasure]);

  const scrollToSection = useCallback((index) => {
    const top = sectionTops.current[index];
    if (top == null) return;
    window.scrollTo({ top: Math.max(0, top - SCROLL_MARGIN), behavior: 'smooth' });
  }, []);

  // Handed out so the hero's heading can jump into the run as well. Only this component
  // knows where a category starts: the gallery has no headings to anchor to, the bands
  // are measured off the tiles.
  useImperativeHandle(ref, () => ({ scrollToSection }), [scrollToSection]);

  return (
    <div className="text-pink-500 pt-20 px-4 sm:px-8 pb-4 sm:pb-8">
      {sections.length > 0 && (
        <RadialSectionNav
          sections={sections}
          activeIndex={activeSection}
          visible={navVisible}
          onSelect={scrollToSection}
          accent="red"
        />
      )}

      <div ref={galleryRef} className="relative z-10 max-w-6xl mx-auto">
        <SectionNavBar
          sections={sections}
          activeIndex={activeSection}
          onSelect={scrollToSection}
          accent="red"
        />
        {instagramHandles.length > 0 && (
          <div className="flex flex-wrap items-center gap-6 mb-8">
            {instagramHandles.map((handle) => (
              <a
                key={handle}
                href={handle}
                target="_blank"
                rel="noopener noreferrer"
                /* The page's own red pair — the one the landing's title is set in, lifting
                   to the lighter one on hover. Spelled out rather than taken from
                   ACCENTS.red because these are fixed classes Tailwind can see; see the
                   note in page_assets/SectionNavBar. */
                className="flex items-center gap-2 text-[#E11D2E] hover:text-[#F2707A] transition-colors duration-200"
                aria-label={`View @${handle.split('/').filter(Boolean).pop()} on Instagram`}
              >
                <Instagram className="w-6 h-6" />
                <span className="text-sm">@{handle.split('/').filter(Boolean).pop()}</span>
              </a>
            ))}
          </div>
        )}

        {images.length > 0 ? (
          <Masonry
            breakpointCols={breakpointColumns}
            // Negative margin plus matching column padding is how this package spaces
            // columns; the tiles supply their own vertical gap.
            className="flex w-auto -ml-3 sm:-ml-6"
            columnClassName="pl-3 sm:pl-6 bg-clip-padding"
          >
            {images.map((image, index) => (
              <ArtPiece
                key={index}
                image={image}
                index={index}
                onOpen={() => setZoomedIndex(index)}
                tileRef={(el) => {
                  tileRefs.current[index] = el;
                }}
                onResize={remeasure}
              />
            ))}
          </Masonry>
        ) : (
          <div className="text-center py-12">
            <p className="text-xl font-serif text-pink-100">Art pieces coming soon...</p>
          </div>
        )}
      </div>

      {zoomedIndex !== null && (
        <Lightbox image={images[zoomedIndex]} onClose={closeLightbox} />
      )}
    </div>
  );
}

export default forwardRef(ArtPortfolioComponent);
